import React, { Fragment } from 'react';
import { LeftMenuUser } from '../LeftMenuUser';
import { CurrentUserLimits } from '../../../../slices/current-user/current-user.state';
import {
    PRACTICE_OWNER_TYPES,
    PRACTICE_STATUS_ENUM,
} from '../../../../utils/constants';
import { PRACTICE_OWNERSHIP_TYPES } from '../../../../utils/constants/PracticeOwnerType.enum';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { FeatureFlag } from '../../../../containers/FeatureFlags/FeatureFlag';
import { FeatureFlagsEnum } from '../../../../containers/FeatureFlags/FeatureFlags.enum';
import { HomeMenuItem } from '../items/HomeMenuItem';
import { MyAccountMenuItem } from '../items/MyAccountMenuItem';
import { NotificationSettingsMenuItem } from '../items/NotificationSettingsMenuItem';
import { MyTeamMembersMenuItem } from '../items/MyTeamMembersMenuItem';
import { MarketplaceDashboardMenuItem } from '../items/MarketplaceDashboardMenuItem';
import { MarketplaceTeamMembersMenuItem } from '../items/MarketplaceTeamMembersMenuItem';
import { MyPracticesMenuItem } from '../items/MyPracticesMenuItem';
import { MyGroupsMenuItem } from '../items/MyGroupsMenuItem';
import { MyReferralHubMenuItem } from '../items/MyReferralHubMenuItem';
import { EcosystemHomepageMenuItem } from '../items/EcosystemHomepageMenuItem';
import { MyDealsMenuItem } from '../items/MyDealsMenuItem';
import { ObservingDealsMenuItem } from '../items/ObservingDealsMenuItem';

const HEADING = 'app-sidebar__heading vertical-nav-menu';
const HEADING_CUSTOM = 'app-sidebar__heading vertical-nav-menu custom';

export interface UserMenuProps {
    user: LeftMenuUser;
    limits: CurrentUserLimits;
}

export const UserMenu = (props: UserMenuProps) => {
    const { user, limits } = props;
    const practices = user.practices || [];
    const isAnyPracticeApproved =
        practices.filter((p) => p.status === PRACTICE_STATUS_ENUM.Approved)
            .length > 0;
    const isAnyActivePracticePending =
        practices.filter(
            (p) =>
                p.status === PRACTICE_STATUS_ENUM.Pending &&
                p.practiceOwnershipType !== PRACTICE_OWNERSHIP_TYPES.FORMER
        ).length > 0;
    const isAnyActivePracticeApproved =
        practices.filter(
            (p) =>
                p.status === PRACTICE_STATUS_ENUM.Approved &&
                p.practiceOwnershipType !== PRACTICE_OWNERSHIP_TYPES.FORMER
        ).length > 0;
    const isMarketplaceBuyer = !!user.isMarketplaceBuyer;
    const isMarketplaceBroker = !!user.isMarketplaceBroker;
    const isReferral = !!user.isReferral;
    const hasReferralRequest = !!user.hasReferralRequest;
    const isFirstTimeUser = practices.length === 0 && !hasReferralRequest;
    const hasPaidSubscription = !!(
        limits &&
        limits.doesSupportSubscriptions &&
        limits.hasPaidSubscription
    );
    const isFormer = user.practiceOwnerType === PRACTICE_OWNER_TYPES.FORMER;
    const engagements = user.engagements || [];
    const charities = user.charities;

    const isReferralOrCharityReferral =
        isReferral && practices.length === 0;
    const showEcosystem =
        (isReferralOrCharityReferral && !charities) ||
        (charities ? charities.length < 1 : false);

    return (
        <Fragment>
            <div id="left-menu" />
            <div>
                <div>
                    {!isReferralOrCharityReferral && (
                        <HomeMenuItem
                            className={
                                isAnyPracticeApproved ||
                                isReferral ||
                                isFirstTimeUser
                                    ? HEADING
                                    : HEADING_CUSTOM
                            }
                        />
                    )}
                    <MyAccountMenuItem />
                    <FeatureFlag flag={FeatureFlagsEnum.FF_Notifications}>
                        {isMarketplaceBuyer && <NotificationSettingsMenuItem />}
                    </FeatureFlag>
                    {isMarketplaceBuyer && <MarketplaceDashboardMenuItem />}
                    <FeatureFlag flag={FeatureFlagsEnum.FF_MrktplcBuyers_3_6}>
                        {isMarketplaceBuyer && <MarketplaceTeamMembersMenuItem />}
                    </FeatureFlag>
                    <FeatureFlag flag={FeatureFlagsEnum.FF_Notifications}>
                        {!isMarketplaceBuyer &&
                            (isAnyPracticeApproved ||
                                isReferral ||
                                isMarketplaceBroker) && (
                                <NotificationSettingsMenuItem />
                            )}
                    </FeatureFlag>
                    {isAnyActivePracticePending && hasPaidSubscription && (
                        <MyTeamMembersMenuItem
                            className={
                                isAnyActivePracticeApproved
                                    ? HEADING
                                    : HEADING_CUSTOM
                            }
                        />
                    )}
                    {!isFirstTimeUser && (
                        <Fragment>
                            {!hasReferralRequest && !isReferral && (
                                <MyPracticesMenuItem
                                    practices={practices}
                                    headingClassName={HEADING}
                                />
                            )}
                            {isFormer && (
                                <MyGroupsMenuItem
                                    summary={user}
                                    limits={limits}
                                    headingClassName={HEADING_CUSTOM}
                                    headingTo="/admin-my-groups"
                                />
                            )}
                            <MyReferralHubMenuItem
                                withFlag={false}
                                className={
                                    isAnyPracticeApproved || isReferral
                                        ? HEADING
                                        : HEADING_CUSTOM
                                }
                            />
                        </Fragment>
                    )}
                </div>
            </div>

            {showEcosystem && <EcosystemHomepageMenuItem />}
            {isMarketplaceBroker &&
                engagements.some((e: any) => e.party === TimelineParty.Broker) && (
                    <MyDealsMenuItem engagements={engagements} />
                )}
            {isMarketplaceBroker &&
                engagements.some(
                    (e: any) => e.party === TimelineParty.Observer
                ) && <ObservingDealsMenuItem engagements={engagements} />}
        </Fragment>
    );
};
